import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './button';
import { Dialog, DialogContent } from './dialog';
import { 
  UserPlus, FileEdit, Calculator, FileSpreadsheet, Presentation, Plus,
  ClipboardCheck, ShieldCheck, UserCheck, GitBranch, Award, CircleCheck,
  FileText, Banknote, Bell, Sparkles, Mail
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Module definitions
const WIN_MODULES = [
  { id: 'lead', label: 'Lead / Referral', icon: UserPlus, path: '/tradie/leads-referral' },
  { id: 'scope', label: 'Generate Scope', icon: FileEdit, path: '/tradie/capture-job-notes' },
  { id: 'costing', label: 'Costing', icon: Calculator, path: null },
  { id: 'quote', label: 'Quote', icon: FileSpreadsheet, path: null },
  { id: 'proposal', label: 'Proposal', icon: Presentation, path: null },
  { id: 'create-win', label: 'Create Your Own', icon: Plus, path: null },
];

const DELIVER_MODULES = [
  { id: 'update', label: 'Site Update', icon: ClipboardCheck, path: '/tradie/log-update' },
  { id: 'swms', label: 'SWMS', icon: ShieldCheck, path: '/tradie/generate-swms' },
  { id: 'signoff', label: 'Record Sign-Off', icon: UserCheck, path: null },
  { id: 'variation', label: 'Variation', icon: GitBranch, path: null },
  { id: 'certificate', label: 'Certificate of Compliance', icon: Award, path: null },
  { id: 'completion', label: 'Job Completion', icon: CircleCheck, path: null },
  { id: 'create-deliver', label: 'Create Your Own', icon: Plus, path: null },
];

const GET_PAID_MODULES = [
  { id: 'claim', label: 'Claim / Invoice', icon: FileText, path: '/tradie/claim' },
  { id: 'payment', label: 'Log Payment', icon: Banknote, path: null },
  { id: 'reminder', label: 'Send Reminder', icon: Bell, path: null },
  { id: 'create-paid', label: 'Create Your Own', icon: Plus, path: null },
];

const ModuleRail = ({ stage, activeModule }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState('');

  // Get modules based on stage
  const getModules = () => {
    switch (stage) {
      case 'win':
        return WIN_MODULES;
      case 'deliver':
        return DELIVER_MODULES;
      case 'get-paid':
        return GET_PAID_MODULES;
      default:
        return [];
    }
  };

  // Get accent color based on stage
  const getAccentColor = () => {
    switch (stage) {
      case 'win':
        return { border: '#10b981', text: 'text-emerald-600', bg: 'emerald' };
      case 'deliver':
        return { border: '#3b82f6', text: 'text-blue-600', bg: 'blue' };
      case 'get-paid':
        return { border: '#f97316', text: 'text-orange-600', bg: 'orange' };
      default:
        return { border: '#64748b', text: 'text-slate-600', bg: 'slate' };
    }
  };

  const modules = getModules();
  const accent = getAccentColor();

  const handleModuleClick = (module) => {
    if (module.path) {
      navigate(module.path);
    } else {
      setSelectedFeature(module.label);
      setShowContactModal(true);
    }
  };

  return (
    <>
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-lg mx-auto overflow-x-auto scrollbar-hide">
          <div className="flex">
            {modules.map((module) => {
              const Icon = module.icon;
              const isActive = activeModule === module.id;
              const isPlaceholder = !module.path;
              
              return (
                <button
                  key={module.id}
                  onClick={() => handleModuleClick(module)}
                  className={`
                    flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap transition-colors
                    ${isActive ? `${accent.text} border-current` : 'border-transparent text-slate-400'}
                    ${isPlaceholder ? 'opacity-60 hover:opacity-80' : 'hover:opacity-80'}
                  `}
                  style={isActive ? { borderColor: accent.border } : {}}
                >
                  <Icon 
                    className="w-4 h-4" 
                    style={isActive ? { color: accent.border } : {}}
                  />
                  <span className="text-sm font-medium">{module.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Feature Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="sm:max-w-sm mx-4 rounded-2xl border-0 p-0 overflow-hidden">
          <div className={`bg-gradient-to-br ${
            stage === 'win' ? 'from-emerald-500 to-emerald-600' :
            stage === 'deliver' ? 'from-blue-500 to-blue-600' :
            'from-orange-500 to-orange-600'
          } px-6 py-5`}>
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {selectedFeature}
              </h2>
            </div>
          </div>
          
          <div className="px-6 py-5">
            <p className="text-slate-600 text-center text-sm mb-5">
              This feature can be added to your 2Tap setup. Get in touch with the team to discuss your requirements.
            </p>
            
            <div className="flex flex-col gap-3">
              <Button
                className={`w-full h-12 text-white font-semibold rounded-xl ${
                  stage === 'win' ? 'bg-emerald-500 hover:bg-emerald-600' :
                  stage === 'deliver' ? 'bg-blue-500 hover:bg-blue-600' :
                  'bg-orange-500 hover:bg-orange-600'
                }`}
                onClick={() => {
                  setShowContactModal(false);
                  const subject = encodeURIComponent(`2Tap Feature Request: ${selectedFeature}`);
                  const body = encodeURIComponent(`Hi 2Tap Team,

I'm interested in the **${selectedFeature}** feature in 2Tap.

Here are my details:
• Name: ${user?.name || 'N/A'}
• Email: ${user?.email || 'N/A'}

Appreciate it — keen to hear what's possible.

Thanks 2Tap!`);
                  window.location.href = `mailto:tradeofficeai@gmail.com?subject=${subject}&body=${body}`;
                }}
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact 2Tap Team
              </Button>
              <Button
                variant="ghost"
                className="w-full h-10 text-slate-500 hover:text-slate-700 font-medium"
                onClick={() => setShowContactModal(false)}
              >
                Maybe later
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModuleRail;
